import { wikimediaThumb } from '../data/parcoursIllustrations'

/**
 * Retrouve l’URL « fichier original » Commons à partir d’une URL /thumb/.../NNNpx-...
 * @param {string} url
 * @returns {string}
 */
export function commonsOriginalFileUrlFromThumbUrl(url) {
  if (!url || typeof url !== 'string' || !url.includes('/commons/thumb/')) return url
  try {
    const u = new URL(url.trim())
    const parts = u.pathname.split('/').filter(Boolean)
    const thumbI = parts.indexOf('thumb')
    if (thumbI < 0) return url
    const afterThumb = parts.slice(thumbI + 1)
    if (afterThumb.length < 2) return url
    const last = afterThumb[afterThumb.length - 1]
    if (!/^\d+px-/.test(last) && !/^lossy-\d+px-/.test(last)) return url
    const pathParts = afterThumb.slice(0, -1)
    u.pathname = `/wikipedia/commons/${pathParts.join('/')}`
    return u.href
  } catch {
    return url
  }
}

/**
 * Miniatures dérivées d’une URL fichier Commons (upload.wikimedia.org/.../commons/.../fichier).
 * Ordre : petites largeurs d’abord, puis variantes lossy (souvent les seules fiables pour les très gros JPG).
 * On évite volontairement l’URL originale : certains fichiers Google Art Project font des dizaines / centaines de Mo.
 *
 * @param {string} fullUrl
 * @returns {string[]}
 */
export function commonsThumbCandidates(fullUrl) {
  if (!fullUrl || typeof fullUrl !== 'string') return []
  let trimmed = fullUrl.trim()
  if (!trimmed.includes('upload.wikimedia.org/wikipedia/commons/')) return [trimmed]

  trimmed = commonsOriginalFileUrlFromThumbUrl(trimmed)

  const m = trimmed.match(/^(https:\/\/upload\.wikimedia\.org\/wikipedia\/commons\/)(.+)$/)
  if (!m) return [trimmed]
  const [, base, path] = m
  const filename = path.replace(/^.*\//, '')

  // Inclure 250 et 500 : beaucoup de fichiers Commons n’exposent pas 480px (ex. portraits Stieler/Beethoven).
  const standardWidths = [128, 220, 250, 320, 480, 500, 800, 1024]
  const lossyWidths = [220, 320, 480, 500, 800]

  const standard = standardWidths.map((w) => wikimediaThumb(trimmed, w))
  const lossy = lossyWidths.map(
    (w) => `${base}thumb/${path}/lossy-${w}px-${filename}`
  )

  const out = []
  const seen = new Set()
  for (const u of [...standard, ...lossy]) {
    if (u && !seen.has(u)) {
      seen.add(u)
      out.push(u)
    }
  }
  return out.length > 0 ? out : [trimmed]
}

/**
 * Chaîne d’affichage pour une tuile avatar Commons : replis limités.
 * L’URL déjà stockée (souvent /thumb/…/NNNpx-…) est essayée en premier : c’est celle validée à l’ajout ;
 * sinon les plus petites largeurs peuvent échouer (429, etc.) avant d’atteindre une variante valide.
 *
 * @param {string} presetUrl
 * @param {string[]} [apiCandidates]
 * @param {{ max?: number }} [opts]
 * @returns {string[]}
 */
export function buildAvatarPresetDisplayCandidates(presetUrl, apiCandidates = [], opts = {}) {
  const max = opts.max ?? 5
  if (!presetUrl || typeof presetUrl !== 'string') return []
  const fromApi = Array.isArray(apiCandidates) && apiCandidates.length > 0 ? [...apiCandidates] : []
  const manual = commonsThumbCandidates(presetUrl)
  let merged = [...new Set([...fromApi, ...manual])]
  const hugeRisk = /Google_Art_Project/i.test(presetUrl)
  if (!hugeRisk && presetUrl) {
    merged = merged.filter((u) => u !== presetUrl)
    merged.unshift(presetUrl)
  }
  return merged.slice(0, max)
}
